<template>
  <!-- Assessment template: key numbers, questions by control objective, settings and history (US 9398) -->
  <div class="us-page">
    <section class="home-hero us-hero at-hero" :class="'at-hero--' + assessment.type">
      <div class="home-hero__main">
        <div class="home-hero__eyebrow at-hero__crumb">
          <router-link :to="{ name: 'maintenance.assessments' }" qid="assessment-back-link">
            <font-awesome-icon icon="chevron-left"/>{{ $t('maintenance.assessments.title') }}
          </router-link>
          <span v-if="assessment.type">·</span>
          <span v-if="assessment.type">
            <font-awesome-icon :icon="typeIcon(assessment.type)"/>
            {{ $t('maintenance.assessments.type.' + assessment.type) }}<template v-if="assessment.scope && assessment.type !== 'control'"> · {{ $t('projects.scope.' + $options.filters.slugify(assessment.scope)) }}</template>
          </span>
        </div>
        <h1 class="home-hero__title at-hero__title" qid="assessment-view-page-title">{{ assessment.name }}</h1>
        <p v-if="assessment.description" class="home-hero__summary at-hero__description">{{ assessment.description }}</p>

        <div class="home-hero__chips">
          <button v-if="canEdit && hasQuestions"
                  type="button"
                  class="home-chip home-chip--light"
                  qid="question-create-button"
                  @click="addQuestion"
          >
            <font-awesome-icon icon="plus"/>{{ $t('maintenance.questions.create_button') }}
          </button>
          <button v-if="canEdit && assessment.type === 'control'"
                  type="button"
                  class="home-chip"
                  qid="control-objective-create-button"
                  @click="addObjective"
          >
            <font-awesome-icon icon="plus"/>{{ $t('template_view.objective.add') }}
          </button>
          <button v-if="canImport"
                  type="button"
                  class="v2-ai-btn api-ai-btn"
                  qid="audit-plan-import-button"
                  @click="openImport"
          >
            <font-awesome-icon icon="sparkles"/>{{ $t('audit_plan_import.button') }}
          </button>
          <!-- Settings and History open from the hero, like on recurring projects -->
          <span class="at-hero__icons">
            <button v-if="canEdit"
                    type="button"
                    class="btn btn-edit"
                    v-b-tooltip.hover
                    :title="$t('templates_page.actions.edit')"
                    :aria-label="$t('templates_page.actions.edit')"
                    qid="edit-a-button"
                    @click="showEditAModal"
            >
              <font-awesome-icon icon="pen"/>
            </button>
            <button v-for="view in heroViews"
                    :key="view.key"
                    type="button"
                    class="btn btn-edit"
                    :class="{ 'btn-edit--on': activeTab === view.key }"
                    v-b-tooltip.hover
                    :title="$t(view.label)"
                    :aria-label="$t(view.label)"
                    :aria-pressed="activeTab === view.key ? 'true' : 'false'"
                    :qid="'assessment-tab-' + view.key"
                    @click="activeTab = view.key"
            >
              <font-awesome-icon :icon="view.icon"/>
            </button>
            <button v-if="canDelete"
                    type="button"
                    class="btn btn-delete"
                    v-b-tooltip.hover
                    :title="$t('templates_page.actions.delete')"
                    :aria-label="$t('templates_page.actions.delete')"
                    qid="delete-a-button"
                    @click="showConfirmDeleteModal"
            >
              <font-awesome-icon icon="trash-alt"/>
            </button>
          </span>
          <GuideButton v-if="getGuide('assessment_template')" :guide="getGuide('assessment_template')"></GuideButton>
        </div>
      </div>

      <div v-if="overview" class="home-week at-stats" qid="assessment-overview">
        <div v-if="hasQuestions" class="at-stat">
          <b>{{ overview.questions }}</b>
          <span>{{ $tc('template_view.stats.questions', overview.questions) }}</span>
        </div>
        <div v-if="assessment.type === 'control'" class="at-stat">
          <b>{{ overview.control_objectives }}</b>
          <span>{{ $tc('template_view.stats.control_objectives', overview.control_objectives) }}</span>
        </div>
        <div v-if="hasQuestions && assessment.type === 'control'" class="at-stat">
          <b>{{ overview.documentation }}</b>
          <span>{{ $t('template_view.stats.documentation') }}</span>
        </div>
        <div class="at-stat">
          <b>{{ overview.projects }}</b>
          <span>{{ $tc('template_view.stats.projects', overview.projects) }}</span>
        </div>
      </div>
    </section>

    <!-- Menu: the same tab bar as projects -->
    <ul v-if="tabs.length" class="nav nav-tabs engagement-tabnav" role="tablist">
      <li v-for="tab in tabs" :key="tab.key" class="nav-item" role="presentation">
        <a href="#"
           class="nav-link"
           :class="{ active: activeTab === tab.key }"
           role="tab"
           :aria-selected="activeTab === tab.key ? 'true' : 'false'"
           :qid="'assessment-tab-' + tab.key"
           @click.prevent="activeTab = tab.key"
        >
          {{ $t(tab.label) }}<span v-if="tab.key === 'questions' && overview" class="at-tab-count">{{ overview.questions }}</span>
        </a>
      </li>
    </ul>

    <!-- Questions -->
    <div v-if="assessment.id && hasQuestions" v-show="activeTab === 'questions'" class="at-cols">
      <QuestionsTable ref="questions" :object="assessment" @updated="refreshOverview" @import="openImport"></QuestionsTable>

      <div class="at-side">
        <div v-if="assessment.type === 'control'" class="v2-card at-panel" qid="assessment-setup">
          <div class="at-panel__head">
            <h3>{{ $t('template_view.setup.title') }}</h3>
            <span class="us-muted">{{ $t('template_view.setup.progress', { done: setupDone, total: setupItems.length }) }}</span>
          </div>
          <button v-for="item in setupItems"
                  :key="item.key"
                  type="button"
                  class="at-check"
                  :qid="'assessment-setup-' + item.key"
                  @click="activeTab = item.tab || 'settings'"
          >
            <span class="at-check__box" :class="{ 'is-on': item.done }">
              <font-awesome-icon v-if="item.done" icon="check"/>
            </span>
            <span class="at-check__text">
              {{ $t(item.label) }}
              <small>{{ item.value }}</small>
            </span>
          </button>
        </div>

        <div class="v2-card at-panel" qid="assessment-recent">
          <div class="at-panel__head">
            <h3>{{ $t('template_view.recent.title') }}</h3>
            <button type="button" class="v2-link at-panel__link" @click="activeTab = 'history'">{{ $t('template_view.recent.all') }}</button>
          </div>
          <ActivityFeed v-if="assessment.id"
                        compact
                        :log="assessment.who_am_i + '::' + assessment.id"
                        :here="assessment"
                        :limit="4"
                        :reload-key="recentKey"
                        :empty-text="$t('template_view.recent.empty')"
          />
        </div>
      </div>
    </div>

    <!-- Default drop-downs: the answer lists tasks can use instead of their own -->
    <div v-if="activeTab === 'defaults'" class="at-defaults" qid="assessment-defaults">
      <div class="us-note us-note--info">
        <font-awesome-icon icon="link"/>
        <span>{{ $t('template_view.defaults.intro') }}</span>
      </div>
      <div class="at-defaults__grid">
        <div v-for="list in shownDefaultLists" :key="list.key" class="v2-card at-panel" :qid="'assessment-default-' + list.key">
          <div class="at-panel__head">
            <div>
              <h3>{{ $t('template_view.defaults.' + list.key + '.title') }}</h3>
              <span class="us-muted">{{ $t('template_view.defaults.' + list.key + '.text') }}</span>
            </div>
            <span class="v2-tag v2-tag--teal at-linked">
              <font-awesome-icon icon="link"/>{{ $tc('template_view.questions_count', linkedCount(list.key), { count: linkedCount(list.key) }) }}
            </span>
          </div>

          <OptionListBuilder v-model="defaults[list.key]"
                             :presets="list.presets"
                             :copy-from="list.key === 'documentation' ? defaults.response : null"
                             :copy-from-label="$t('template_view.defaults.copy_response')"
                             :disabled="!canEdit"
          ></OptionListBuilder>

          <div v-if="removedOptions(list.key).length && linkedCount(list.key)" class="us-note at-defaults__warning">
            <font-awesome-icon icon="exclamation-triangle"/>
            <span>{{ $tc('template_view.defaults.removed', removedOptions(list.key).length, { names: removedOptions(list.key).join(', '), count: linkedCount(list.key) }) }}</span>
          </div>

          <div v-if="canEdit && defaultsChanged(list.key)" class="at-defaults__foot">
            <b-button variant="secondary" :qid="'assessment-default-discard-' + list.key" @click="discardDefaults(list.key)">
              {{ $t('template_view.defaults.discard') }}
            </b-button>
            <b-button variant="success"
                      :disabled="savingDefaults === list.key"
                      :qid="'assessment-default-save-' + list.key"
                      @click="saveDefaults(list.key)"
            >
              <font-awesome-icon :icon="savingDefaults === list.key ? 'spinner' : 'check'" :class="{ 'fa-spin': savingDefaults === list.key }" class="mr-1"/>
              {{ linkedCount(list.key) ? $tc('template_view.defaults.save_update', linkedCount(list.key), { count: linkedCount(list.key) }) : $t('template_view.defaults.save') }}
            </b-button>
          </div>
        </div>
      </div>
    </div>

    <!-- Settings -->
    <div v-if="activeTab === 'settings'" class="at-settings">
      <div v-if="assessment.type === 'control'" class="v2-card at-panel" qid="assessment-engagement-settings">
        <div class="at-panel__head">
          <h3>{{ $t('maintenance.assessments.configurations_title') }}</h3>
          <GuideButton v-if="getGuide('assessment_template_measures')" :guide="getGuide('assessment_template_measures')"></GuideButton>
        </div>

        <div v-for="setting in toggleSettings" :key="setting.field" class="at-setting">
          <div class="at-setting__text">
            <b>{{ $t(setting.label) }}</b>
            <small v-if="setting.description">{{ $t(setting.description) }}</small>
          </div>
          <button type="button"
                  class="us-toggle us-toggle--teal"
                  :class="{ 'is-on': settingOn(setting.field) }"
                  role="switch"
                  :disabled="!canEdit || savingField === setting.field"
                  :aria-checked="settingOn(setting.field) ? 'true' : 'false'"
                  :aria-label="$t(setting.label)"
                  :qid="'assessment-setting-' + setting.field"
                  @click="saveSetting(setting.field, settingOn(setting.field) ? 0 : 1)"
          ></button>
        </div>

        <div class="at-setting">
          <div class="at-setting__text">
            <b>{{ $t('system.required_sign_offs') }}</b>
            <small>{{ $t('system.required_sign_offs_description') }}</small>
          </div>
          <NumberStepper v-if="canEdit" v-model="requiredSignOffs" qid="assessment-required-sign-offs" @input="saveSignOffs"></NumberStepper>
          <b v-else qid="assessment-required-sign-offs-value">{{ assessment.required_sign_offs || 0 }}</b>
        </div>

        <div class="at-setting at-setting--block">
          <div class="at-setting__row">
            <div class="at-setting__text">
              <b>{{ $t('projects.engagement.settings.template_title') }}</b>
              <small>{{ $t('projects.engagement.settings.template_description') }}</small>
            </div>
            <template v-if="canEdit">
              <b-button v-if="!showEngagementStepsForm" variant="secondary" size="sm" qid="assessment-engagement-steps-edit-button" @click="showEngagementStepsFormFunction">
                <font-awesome-icon icon="pen" class="mr-1"/>{{ $t('system.edit') }}
              </b-button>
              <span v-else class="at-setting__actions">
                <b-button variant="secondary" size="sm" qid="assessment-engagement-steps-cancel-button" @click="cancelEngagementSteps">
                  {{ $t('processes.legal_basis.create.cancel') }}
                </b-button>
                <b-button variant="success" size="sm" qid="assessment-engagement-steps-submit-button" @click="formEngagementStepsSubmit">
                  {{ $t('processes.legal_basis.create.submit') }}
                </b-button>
              </span>
            </template>
          </div>
          <div qid="assessment-engagement-steps">
            <EngagementStepsPicker v-model="engagementSteps" :disabled="!showEngagementStepsForm"></EngagementStepsPicker>
          </div>
        </div>
      </div>

      <div v-if="assessment.type === 'control' || assessment.type === 'risk'" class="v2-card at-panel" qid="assessment-report-template">
        <div class="at-panel__head">
          <h3>{{ $t('maintenance.assessments.create.upload_report_label') }}</h3>
          <GuideButton v-if="getGuide('report_template')" :guide="getGuide('report_template')"></GuideButton>
        </div>
        <b-alert v-if="error.length" show variant="danger" qid="create-q-error">{{ error }}</b-alert>
        <div v-for="file in templateFiles" :key="file.id" class="at-file">
          <font-awesome-icon icon="file-word"/>
          <a href="#" qid="create-q-media-item" @click.prevent="downloadMedia(file)">{{ file.file_name }}</a>
          <span class="us-grow"></span>
          <button type="button" class="us-icon-btn" :aria-label="$t('template_view.report.download')" qid="download-media" @click="downloadMedia(file)">
            <font-awesome-icon icon="cloud-download-alt"/>
          </button>
          <button v-if="canEdit" type="button" class="us-icon-btn" :aria-label="$t('template_view.report.remove')" qid="remove-media-item" @click="openDeleteAttachmentModal(file)">
            <font-awesome-icon icon="trash-alt"/>
          </button>
        </div>
        <div v-if="canEdit" class="at-drop" :class="{ 'is-active': showDropActive }">
          <font-awesome-icon icon="cloud-upload-alt"/>
          <span>
            <file-upload ref="uploader-template"
                         v-model="files"
                         :drop="true"
                         input-id="template-file"
                         :extensions="extensions"
                         :accept="accept"
                         :multiple="false"
                         class="v2-link"
                         @input="onFileChangeEdit"
                         @dropActive="showDropActiveFunction"
            >{{ $t('template_view.report.choose') }}</file-upload>
            {{ $t('template_view.report.hint') }}
          </span>
        </div>
      </div>

    </div>

    <!-- History -->
    <div v-if="activeTab === 'history'">
      <AssessmentHistory :object="assessment"></AssessmentHistory>
    </div>

    <!-- Edit template -->
    <b-modal ref="edit-assessment" modal-class="v2-modal" hide-footer @hide="buttonDisabled = false">
      <template #modal-header>
        <div class="us-modal-head">
          <div>
            <h5 class="mb-0" qid="update-a-title">{{ $t('maintenance.assessments.update.title') }}</h5>
            <GuideButton v-if="getGuide('update_assessment_template')" :guide="getGuide('update_assessment_template')"></GuideButton>
          </div>
          <button type="button" class="us-icon-btn" :aria-label="$t('maintenance.assessments.create.cancel')" @click="hideEditAModal">
            <font-awesome-icon icon="times"/>
          </button>
        </div>
      </template>

      <b-alert v-if="error.length" show variant="danger" qid="edit-a-error">{{ error }}</b-alert>

      <b-form class="us-form" qid="edit-a-form" @submit="editAFormSubmit">
        <b-form-group class="mb-0">
          <label class="us-field-label" for="edit-a-name">{{ $t('maintenance.assessments.create.name_label') }}<span class="mandatory"> *</span></label>
          <b-form-input id="edit-a-name"
                        v-model="form.name"
                        type="text"
                        :maxLength="255"
                        required
                        :placeholder="$t('maintenance.assessments.create.name_placeholder')"
                        qid="edit-a-form-name"
          ></b-form-input>
        </b-form-group>
        <b-form-group class="mb-0">
          <label class="us-field-label" for="edit-a-description">{{ $t('maintenance.assessments.create.description_label') }}<span class="mandatory"> *</span></label>
          <b-form-textarea id="edit-a-description"
                           v-model="form.description"
                           rows="4"
                           max-rows="8"
                           required
                           :placeholder="$t('maintenance.assessments.create.description_placeholder')"
                           qid="edit-a-form-description"
          ></b-form-textarea>
        </b-form-group>
        <b-form-group v-if="assessment.type === 'risk' || assessment.type === 'bia'" class="mb-0">
          <label class="us-field-label">{{ $t('projects.create.scope_label') }}<span class="mandatory"> *</span></label>
          <multiselect v-model="scope"
                       :options="scopesFor(assessment.type)"
                       :placeholder="$t('projects.create.scope_placeholder')"
                       label="name"
                       track-by="value"
                       :select-label="$t('system.dropdown_select_label')"
                       :selected-label="$t('system.dropdown_selected_label')"
                       :deselect-label="$t('system.dropdown_deselect_label')"
                       qid="create-a-form-scope"
          ></multiselect>
        </b-form-group>
        <div class="us-modal-foot">
          <span class="us-grow"></span>
          <b-button variant="secondary" qid="edit-a-cancel-button" @click="hideEditAModal">
            {{ $t('maintenance.assessments.create.cancel') }}
          </b-button>
          <b-button type="submit" variant="success" :disabled="buttonDisabled" qid="edit-a-submit-button">
            <font-awesome-icon v-if="buttonDisabled" icon="spinner" size="sm" class="fa-spin mr-1"/>
            {{ $t('maintenance.assessments.create.submit') }}
          </b-button>
        </div>
      </b-form>
    </b-modal>

    <!-- Delete template -->
    <b-modal ref="delete-assessment" modal-class="v2-modal" hide-footer>
      <template #modal-header>
        <div class="us-modal-head">
          <h5 class="mb-0" qid="delete-a-title">{{ $t('maintenance.assessments.delete.title') }}</h5>
          <button type="button" class="us-icon-btn" :aria-label="$t('maintenance.assessments.delete.cancel')" @click="hideConfirmDeleteModal">
            <font-awesome-icon icon="times"/>
          </button>
        </div>
      </template>
      <div class="us-remove">
        <span class="at-icon" :class="'at-icon--' + assessment.type"><font-awesome-icon :icon="typeIcon(assessment.type)"/></span>
        <div>
          <b>{{ assessment.name }}</b>
          <div v-if="overview" class="us-muted">{{ $tc('template_view.questions_count', overview.questions, { count: overview.questions }) }}</div>
        </div>
      </div>
      <p class="mt-3 mb-0">{{ $t('maintenance.assessments.delete.are_you_sure') }}</p>
      <div class="us-modal-foot">
        <span class="us-grow"></span>
        <b-button variant="secondary" qid="delete-a-cancel-button" @click="hideConfirmDeleteModal">
          {{ $t('maintenance.assessments.delete.cancel') }}
        </b-button>
        <b-button variant="danger" :disabled="buttonDisabled" qid="delete-a-submit-button" @click="confirmDeleteA">
          {{ $t('maintenance.assessments.delete.submit') }}
        </b-button>
      </div>
    </b-modal>

    <AuditPlanImportDialog v-if="assessment.id && canImport"
                           ref="auditPlanImport"
                           :assessment="assessment"
                           :has-controls="!!(overview && (overview.questions || overview.control_objectives))"
                           :in-use="!!(overview && overview.projects)"
                           @imported="imported"
    ></AuditPlanImportDialog>

    <ConfirmRemoveAttachment
        :object="selectedFile"
        :show="showDeleteAttachmentModal"
        type="file"
        @closeConfirmModal="closeDeleteAttachmentModal"
    ></ConfirmRemoveAttachment>
  </div>
</template>

<script>
import piincHttp from '@/util/configureAxios.js'
import QuestionsTable from '@/views/Assessments/QuestionsTable.vue';
import AssessmentHistory from '@/views/Assessments/AssessmentHistory.vue';
import ActivityFeed from '@/components/activity/feed/ActivityFeed.vue';
import ConfirmRemoveAttachment from '@/components/ConfirmRemoveAttachment.vue';
import GuideButton from '@/components/guide/GuideButton.vue';
import NumberStepper from '@/components/NumberStepper.vue';
import EngagementStepsPicker from '@/views/Projects/Engagement/EngagementStepsPicker.vue';
import OptionListBuilder from '@/components/assessment/OptionListBuilder.vue';
import AuditPlanImportDialog from '@/components/assessment/auditPlanImport/AuditPlanImportDialog.vue';
import { parseOptions } from '@/util/dropdownOptions.js';
import { allSteps } from '@/views/Projects/Engagement/engagement.js';
import { TEMPLATE_TYPE_ICONS } from '@/util/templateTypes.js';

export default {
  name: 'AssessmentView',
  data: function() {
    return {
      accept: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      extensions: 'docx',

      files: [],
      selectedFile: {},
      showDropActive: false,
      showDeleteAttachmentModal: false,

      assessment: {},
      overview: null,
      // Changes when the template is loaded again, so the latest changes are read again
      recentKey: 0,
      activeTab: 'questions',
      form: {
        slug: '',
        name: '',
        description: ''
      },
      scope: null,
      riskScopes: [
        { name: this.$t('projects.scope.process'), value: 'process' },
        { name: this.$t('projects.scope.activity'), value: 'activity' },
        { name: this.$t('projects.scope.third_party'), value: 'third_party' }
      ],
      biaScopes: [
        { name: this.$t('projects.scope.process'), value: 'process' },
        { name: this.$t('projects.scope.activity'), value: 'activity' }
      ],
      // Yes/no settings of global templates, saved as soon as they are switched
      toggleSettings: [
        { field: 'include_independence_declaration', label: 'maintenance.assessments.require_independence_declaration_title' },
        { field: 'require_engagement_acceptance', label: 'maintenance.assessments.require_engagement_acceptance_title', description: 'maintenance.assessments.require_engagement_acceptance_description' },
        { field: 'include_mapping', label: 'maintenance.assessments.include_mapping_title' },
        { field: 'include_relations', label: 'projects.create.include_relations' }
      ],
      savingField: '',
      defaults: { response: [], documentation: [] },
      defaultLists: [
        { key: 'response', field: 'default_response_options', presets: ['effective', 'yes_no', 'impact', 'maturity'] },
        { key: 'documentation', field: 'default_response_options_doc', presets: ['received', 'yes_no'] }
      ],
      savingDefaults: '',
      requiredSignOffs: 0,
      showEngagementStepsForm: false,
      engagementSteps: [],
      error: '',
      buttonDisabled: false,
      myGuides: []
    }
  },
  computed: {
    // Contextual (risk) templates collect no documentation, so they have no default drop-down for it (US 9480)
    shownDefaultLists() {
      return this.assessment.type === 'risk' ? this.defaultLists.filter(list => list.key !== 'documentation') : this.defaultLists;
    },
    // Audit plans are read by the AI, so importing is part of the AI module (US 9404)
    canImport() {
      return this.canEdit && this.assessment.type === 'control' && !!this.getClient.aiModule;
    },
    getClient() {
      return this.$store.state.client
    },
    canEdit() {
      return this.isGranted([this.permissions.ASSESSMENT_EDIT, this.permissions.ASSESSMENT_EDIT_OWN], this.assessment.created_by) && (!this.getClient.parent || (this.getClient.parent && this.assessment.partition));
    },
    canDelete() {
      return this.isGranted([this.permissions.ASSESSMENT_DELETE, this.permissions.ASSESSMENT_DELETE_OWN], this.assessment.created_by) && (!this.getClient.parent || (this.getClient.parent && this.assessment.partition));
    },
    // BIA templates hold the impact assessment, not questions
    hasQuestions() {
      return !!this.assessment.question && this.assessment.type !== 'bia';
    },
    tabs() {
      return this.hasQuestions ? [
        { key: 'questions', label: 'maintenance.questions.label' },
        { key: 'defaults', label: 'template_view.tabs.defaults' }
      ] : [];
    },
    // Opened from the hero instead of the menu
    heroViews() {
      let views = [];
      if (this.assessment.type === 'control' || this.assessment.type === 'risk') {
        views.push({ key: 'settings', label: 'template_view.tabs.settings', icon: 'cog' });
      }
      views.push({ key: 'history', label: 'activities.tab.history', icon: 'history' });
      return views;
    },
    views() {
      return this.tabs.concat(this.heroViews);
    },
    templateFiles() {
      return (this.assessment.media || []).filter(file => file.collection_name == 'template');
    },
    setupItems() {
      let steps = Array.isArray(this.assessment.engagement_steps) ? this.assessment.engagement_steps.length : allSteps().length;

      let defaults = parseOptions(this.assessment.default_response_options).length;

      return [
        { key: 'defaults', tab: 'defaults', label: 'template_view.tabs.defaults', done: defaults > 0, value: defaults ? this.$tc('template_view.setup.options', defaults, { count: defaults }) : this.$t('template_view.setup.no_defaults') },
        { key: 'sign_offs', label: 'system.required_sign_offs', done: this.assessment.required_sign_offs > 0, value: this.$tc('template_view.setup.sign_offs', this.assessment.required_sign_offs || 0, { count: this.assessment.required_sign_offs || 0 }) },
        { key: 'independence', label: 'maintenance.assessments.require_independence_declaration_title', done: this.settingOn('include_independence_declaration'), value: this.onOff('include_independence_declaration') },
        { key: 'acceptance', label: 'maintenance.assessments.require_engagement_acceptance_title', done: this.settingOn('require_engagement_acceptance'), value: this.onOff('require_engagement_acceptance') },
        { key: 'steps', label: 'projects.engagement.settings.template_title', done: steps > 0, value: this.$tc('template_view.setup.steps', steps, { count: steps }) },
        { key: 'mapping', label: 'maintenance.assessments.include_mapping_title', done: this.settingOn('include_mapping'), value: this.onOff('include_mapping') },
        { key: 'relations', label: 'projects.create.include_relations', done: this.settingOn('include_relations'), value: this.onOff('include_relations') },
        { key: 'report', label: 'maintenance.assessments.create.upload_report_label', done: this.templateFiles.length > 0, value: this.templateFiles.length ? this.templateFiles[0].file_name : this.$t('template_view.setup.no_report') }
      ];
    },
    setupDone() {
      return this.setupItems.filter(item => item.done).length;
    }
  },
  watch: {
    getClient: function (newValue, oldValue) {
      if (newValue && oldValue && newValue.slug !== oldValue.slug) {
        this.goToList();
      } else {
        this.loadContent();
      }
    },
    views(views) {
      if (!_.find(views, { key: this.activeTab })) {
        this.activeTab = views[0].key;
      }
    }
  },
  components: {
    QuestionsTable,
    AssessmentHistory,
    ActivityFeed,
    ConfirmRemoveAttachment,
    GuideButton,
    NumberStepper,
    EngagementStepsPicker,
    OptionListBuilder,
    AuditPlanImportDialog
  },
  methods: {
    goToList() {
      this.$router.push({ name: 'maintenance.assessments' });
    },
    typeIcon(type) {
      return TEMPLATE_TYPE_ICONS[type] || 'file-alt';
    },
    scopesFor(type) {
      let scopes = type === 'bia' ? this.biaScopes : this.riskScopes.slice();
      if (type !== 'bia' && this.getClient.systemModule) {
        scopes.push({ name: this.$t('projects.scope.system'), value: 'system' });
      }
      return scopes;
    },
    // Engagement acceptance is on unless the template switched it off; the others are off unless switched on
    settingOn(field) {
      let value = this.assessment[field];
      if (field === 'require_engagement_acceptance') {
        return value !== false && value !== 0;
      }
      return value == 1;
    },
    onOff(field) {
      return this.settingOn(field) ? this.$t('system.yes') : this.$t('system.no');
    },
    linkedCount(list) {
      if (!this.overview) {
        return 0;
      }
      return list === 'documentation' ? this.overview.linked_options_doc : this.overview.linked_options;
    },
    savedDefaults(list) {
      return parseOptions(this.assessment[_.find(this.defaultLists, { key: list }).field]);
    },
    defaultsChanged(list) {
      return JSON.stringify(this.defaults[list]) !== JSON.stringify(this.savedDefaults(list));
    },
    // Saved options that the draft no longer has; linked tasks will lose them
    removedOptions(list) {
      let titles = this.defaults[list].map(option => option.title.trim());
      return this.savedDefaults(list).map(option => option.title).filter(title => titles.indexOf(title.trim()) === -1);
    },
    discardDefaults(list) {
      this.defaults[list] = this.savedDefaults(list);
    },
    saveDefaults(list) {
      let self = this;
      let options = this.defaults[list].filter(option => option.title.trim());
      this.savingDefaults = list;

      piincHttp.put('assessments/' + this.assessment.slug + '/default-options', {
        list: list,
        options: options,
        client: this.getClient.slug
      }).then(function(response) {
        self.savingDefaults = '';
        self.assessment[_.find(self.defaultLists, { key: list }).field] = response.data.options.length ? JSON.stringify(response.data.options) : null;
        self.defaults[list] = response.data.options;
        self.loadContent();
        self.$toastr('success', response.data.questions
          ? self.$tc('template_view.defaults.saved_updated', response.data.questions, { count: response.data.questions })
          : self.$t('template_view.defaults.saved'));
      }, function(error) {
        self.savingDefaults = '';
        self.$toastr('error', error.data && error.data.message ? error.data.message : self.$t('users_page.error'));
      });
    },
    addQuestion() {
      this.activeTab = 'questions';
      this.$nextTick(() => this.$refs.questions.showCreateQForm());
    },
    addObjective() {
      this.activeTab = 'questions';
      this.$nextTick(() => this.$refs.questions.showCreateControlObjectiveModal());
    },
    openImport() {
      this.$refs.auditPlanImport.open();
    },
    // After an audit plan was saved into the template: the new objectives and controls, numbers and history
    imported(counts) {
      this.activeTab = 'questions';
      this.$refs.questions.showImported(counts.objective_ids);
      this.$toastr('success', this.$t('audit_plan_import.saved', counts));
    },
    showDropActiveFunction(value) {
      this.showDropActive = value;
    },
    saveSetting(field, value) {
      let self = this;
      this.savingField = field;

      piincHttp.put('assessments/' + this.assessment.slug, { [field]: value }).then(function() {
        self.savingField = '';
        self.loadContent();
        self.$toastr('success', self.$t('maintenance.assessments.update.success'));
      }, function(error) {
        self.savingField = '';
        self.$toastr('error', error.data && error.data.message ? error.data.message : self.$t('users_page.error'));
      });
    },
    saveSignOffs: _.debounce(function(value) {
      if (value !== (this.assessment.required_sign_offs || 0)) {
        this.saveSetting('required_sign_offs', value);
      }
    }, 600),
    templateEngagementSteps() {
      // No setting on the template means all steps are included
      return Array.isArray(this.assessment.engagement_steps) ? this.assessment.engagement_steps.slice() : allSteps();
    },
    showEngagementStepsFormFunction() {
      this.engagementSteps = this.templateEngagementSteps();
      this.showEngagementStepsForm = true;
    },
    cancelEngagementSteps() {
      this.engagementSteps = this.templateEngagementSteps();
      this.showEngagementStepsForm = false;
    },
    formEngagementStepsSubmit() {
      this.showEngagementStepsForm = false;
      this.saveSetting('engagement_steps', this.engagementSteps);
    },
    onFileChangeEdit() {
      let self = this;
      let formData = new FormData();
      let sizeError = false;

      for (let i = 0; i < this.files.length; i++) {
        // 30 MB
        if (this.files[i].size > 31457280) {
          sizeError = true;
        }
        formData.append("document[]", this.files[i].file);
      }

      formData.append("name", "documents");
      formData.append("collection", "template");
      formData.append("model_type", this.assessment.who_am_i);
      formData.append("model_id", this.assessment.id);

      if (sizeError) {
        this.error = this.$t('system.file_size_30');
        this.files = [];
      } else {
        piincHttp.post('documents', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }).then(function() {
          self.$toastr('success', self.$t('maintenance.third_parties.media_add_success'));
          self.error = '';
          self.loadContent();
          self.files = [];
        }, function(error) {
          self.error = error.data.message;
          if (error.data.errors && error.data.errors['document.0']) {
            self.error += ' ' + error.data.errors['document.0'][0];
          }
        });
      }
    },
    openDeleteAttachmentModal(object) {
      this.selectedFile = object;
      this.showDeleteAttachmentModal = true;
    },
    closeDeleteAttachmentModal() {
      this.selectedFile = {};
      this.showDeleteAttachmentModal = false;
      this.loadContent();
    },
    downloadMedia(object) {
      piincHttp.get('documents/' + object.id + '/download', {responseType: 'blob'}).then(function(response) {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.download = object.file_name;

        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      }, function() {});
    },
    getGuide(code) {
      return _.find(this.myGuides, function(object) {
        return object.scope == code && object.type == 'Maintenance' && object.enabled;
      });
    },
    loadGuides(code) {
      let self = this;
      piincHttp.get('guides', { params: { per_page: 1, language: 'en', type: 'Maintenance', scope: code, client: this.getClient.slug } }).then(function(response) {
        if (response.data.items.length) {
          self.myGuides.push(response.data.items[0]);
        }
      }, function() {});
    },
    loadContent() {
      let self = this;
      piincHttp.get('assessments/' + this.$route.params.slug, { params: {
        client: this.getClient.slug,
        with_overview: 1
      }}).then(function(response) {
        let first = !self.assessment.id;
        let dirty = {
          response: !first && self.defaultsChanged('response'),
          documentation: !first && self.defaultsChanged('documentation')
        };

        self.assessment = response.data;
        self.overview = response.data.overview || null;
        self.requiredSignOffs = self.assessment.required_sign_offs || 0;
        if (!self.showEngagementStepsForm) {
          self.engagementSteps = self.templateEngagementSteps();
        }
        // Unsaved edits to a default drop-down survive a reload of the template, e.g. after moving an objective
        self.defaults = {
          response: dirty.response ? self.defaults.response : self.savedDefaults('response'),
          documentation: dirty.documentation ? self.defaults.documentation : self.savedDefaults('documentation')
        };
        if (first && !self.hasQuestions) {
          self.activeTab = self.views[0].key;
        }
        self.loadRecent();
        if (first) {
          self.$nextTick(() => self.openImportFromLink());
        }
      }, function() {});
    },
    // The "audit plan read" notification links here with ?import=<id>: open the dialog on that import
    openImportFromLink() {
      let id = this.$route.query.import;
      if (!id || !this.$refs.auditPlanImport) {
        return;
      }
      // Out of the address bar, so a reload doesn't open it again. Not through the router: the page is keyed by
      // its full path, so a router change would build it anew and close the dialog
      let url = new URL(window.location.href);
      url.searchParams.delete('import');
      window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
      this.$refs.auditPlanImport.open(id);
    },
    loadRecent() {
      this.recentKey += 1;
    },
    // After questions or objectives change: new numbers in the header and the change in the side panel
    refreshOverview() {
      this.loadContent();
    },
    showConfirmDeleteModal() {
      this.$refs['delete-assessment'].show();
    },
    hideConfirmDeleteModal() {
      this.$refs['delete-assessment'].hide();
    },
    confirmDeleteA() {
      let self = this;
      this.buttonDisabled = true;

      piincHttp.delete('assessments/' + this.assessment.slug).then(function() {
        self.buttonDisabled = false;
        self.hideConfirmDeleteModal();
        self.goToList();
        self.$toastr('success', self.$t('maintenance.assessments.delete.success'));
      }, function() {
        self.buttonDisabled = false;
      });
    },
    showEditAModal() {
      this.error = '';
      this.form = { slug: this.assessment.slug, name: this.assessment.name, description: this.assessment.description };
      this.scope = _.find(this.scopesFor(this.assessment.type), { value: this.assessment.scope }) || null;
      this.loadGuides('update_assessment_template');
      this.$refs['edit-assessment'].show();
    },
    hideEditAModal() {
      this.$refs['edit-assessment'].hide();
    },
    editAFormSubmit(evt) {
      let self = this;
      evt.preventDefault();

      if (this.assessment.type !== 'control' && !(this.scope && this.scope.value)) {
        this.error = this.$t('maintenance.assessments.create.scope_error_message');
        return;
      }

      this.error = '';
      this.buttonDisabled = true;
      piincHttp.put('assessments/' + this.form.slug, {
        name: this.form.name,
        description: this.form.description,
        type: this.assessment.type,
        scope: this.assessment.type !== 'control' && this.scope ? this.scope.value : '',
        client: this.getClient.slug
      }).then(function(response) {
        self.buttonDisabled = false;
        self.hideEditAModal();
        if (response.data.slug === self.assessment.slug) {
          self.loadContent();
        } else {
          self.$router.push({ name: 'maintenance.assessments.view', params: { slug: response.data.slug } });
        }
        self.$toastr('success', self.$t('maintenance.assessments.update.success'));
      }, function(error) {
        self.buttonDisabled = false;
        self.error = error.data && error.data.message ? error.data.message : '';
      });
    }
  },
  created () {
    if (this.getClient && this.getClient.slug) {
      this.loadContent();
    }
    this.loadGuides('assessment_template');
    this.loadGuides('report_template');
    this.loadGuides('assessment_template_measures');
  }
}
</script>
